import * as React from 'react';

import {
  DimeTooltipContent,
  DimeTooltipProvider,
  DimeTooltipRoot,
  DimeTooltipTrigger,
} from './DimeTooltip.js';
import { useOpenOnTap } from './lib/use-open-on-tap.js';
import { UndescribedSpan } from './UndescribedSpan.js';

const FOCUSABLE_WRAPPER = { tabIndex: 0 };
const PRESENTATIONAL_WRAPPER = { role: 'none' };

type DisabledTooltipProps = Readonly<{
  tooltip: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  /** Inside a menu the wrapper must stay out of the tab order and the accessibility tree, since a menu may only own menuitems. */
  focusable?: boolean;
  children: React.ReactNode;
}>;

/** A disabled control takes no pointer events, so this wrapper holds its tooltip and also opens it on a touch tap, since touch has no hover. */
export function DisabledTooltip({
  tooltip,
  side = 'top',
  className,
  focusable = true,
  children,
}: DisabledTooltipProps) {
  const { open, onOpenChange, triggerProps } = useOpenOnTap();
  /** Radix would otherwise point a presentational wrapper's aria-describedby at the open tooltip, which re-exposes it to assistive tech. */
  const Wrapper = focusable ? 'span' : UndescribedSpan;

  return (
    <DimeTooltipProvider delayDuration={200}>
      <DimeTooltipRoot open={open} onOpenChange={onOpenChange}>
        <DimeTooltipTrigger
          asChild
          {...(focusable ? FOCUSABLE_WRAPPER : PRESENTATIONAL_WRAPPER)}
          {...triggerProps}
        >
          <Wrapper className={className}>{children}</Wrapper>
        </DimeTooltipTrigger>
        <DimeTooltipContent side={side} align="center">
          {tooltip}
        </DimeTooltipContent>
      </DimeTooltipRoot>
    </DimeTooltipProvider>
  );
}
